/**
 * makeRenderWidget — build a Grid `renderItem` bound to a grid + preview mode.
 *
 * Returns the catalog-dispatch closure that both the PAGE grid (PageNode) and
 * each section's NESTED grid (GridWidget) hand to <Grid renderItem={...}>. The
 * nested grid was previously rendered without a renderItem, so its
 * text/image/form widgets fell back to Grid's raw `contentProps.content` path
 * and never mounted (you couldn't write text or upload images inside a
 * section) — this is what makes them editable again.
 *
 * `catalog` is referenced INSIDE the returned closure (call-time), never at
 * module load, so the renderWidget → catalog → SectionWidget → GridWidget →
 * renderWidget import cycle is deferred-usage and safe under ESM/Vite.
 */
import { html, css } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { microSpacing, radii } from "@duro-app/tokens/tokens/spacing.css";
import { typography } from "@duro-app/tokens/tokens/typography.css";
import { useRef } from "react";
import type {
  DragEvent as ReactDragEvent,
  PointerEvent as ReactPointerEvent,
  ReactNode,
  ReactElement,
} from "react";
import {
  DefaultGridItem,
  useGridStore,
  type GridItemRenderArgs,
} from "@fredericrous/grid";
import { resolveWidget } from "./catalog.js";
import { registeredWidgetQuickEdit } from "./catalogRegistry.js";
import { openQuickEdit } from "../quickEditTarget.js";
import { DropReveal } from "./dropReveal.js";
import { MobileItemChrome } from "../MobileItemChrome.js";
import { usePageEdit } from "../PageEditContext.js";
import { useEditRequest } from "../editRequest.js";
import { useSitemapInteraction } from "../SitemapInteractionContext.js";
import { useAltHeld } from "../altHeld.js";
import { useSiteId } from "../SiteContext.js";
import { prepareDragImage, type DragImage } from "../cloneItemNode.js";
import {
  WB_ITEM_TYPE,
  buildCrossTabPayload,
  encodeMetaType,
  encodePayload,
  isCrossTabDraggable,
  textFallbackOf,
} from "../crossTabDrag.js";

const styles = css.create({
  unknown: {
    paddingTop: "0.25rem",
    paddingBottom: "0.25rem",
    paddingLeft: "0.25rem",
    paddingRight: "0.25rem",
    color: colors.textMuted,
    fontSize: typography.fontSizeXs,
  },
  content: {
    position: "relative",
    width: "100%",
    height: "100%",
  },
  // A widget's `valign` (buttons): placed in the cell as the published page
  // places it (renderPage's cellPlacement), as the box's align-content.
  middle: { alignContent: "center" },
  bottom: { alignContent: "end" },
  // ⌥ armed: the OS copy affordance BEFORE the native drag starts
  // (descendant cursor beats the Box's grab cursor).
  contentArmed: {
    cursor: "copy",
  },
  copyBadge: {
    position: "absolute",
    top: 6,
    right: 6,
    zIndex: 5,
    paddingTop: microSpacing.px2,
    paddingBottom: microSpacing.px2,
    paddingLeft: "0.5rem",
    paddingRight: "0.5rem",
    borderRadius: radii.full,
    backgroundColor: colors.contrastSurface,
    color: colors.onContrastSurface,
    fontSize: 11,
    fontWeight: typography.fontWeightSemibold,
    lineHeight: 1.6,
    pointerEvents: "none",
  },
  // Zero-size, never painted: only a DOM foothold for the bridge's lookup.
  bridge: {
    position: "absolute",
    width: 0,
    height: 0,
    pointerEvents: "none",
  },
});

/**
 * The selection bar's Edit / Crop / Add media / Replace verb, delivered to
 * the widget that owns `itemId` without the widget knowing about the bar:
 * text and filled images drill in through the page's edit context (the same
 * path their second click takes); empty or replaceable media opens the file
 * picker the widget already renders. Lives here, in the wrapper every widget
 * shares, so no widget file has to change to gain the verb.
 */
function EditRequestBridge({
  itemId,
  contentType,
  contentProps,
}: {
  itemId: string;
  contentType: string;
  contentProps: Record<string, unknown> | undefined;
}): ReactElement {
  const { setEditingItemId } = usePageEdit();
  const anchor = useRef<HTMLDivElement | null>(null);
  const hasMedia = !!(contentProps?.["sha256"] || contentProps?.["url"]);
  useEditRequest(itemId, () => {
    const drillIn =
      contentType === "text" ||
      contentType === "heading" ||
      (contentType === "image" && hasMedia);
    if (drillIn) {
      setEditingItemId(itemId);
      return;
    }
    // Media kinds: the widget's own hidden <input type="file"> (web only —
    // on native the anchor is a host view with no DOM to walk).
    const el = anchor.current as { closest?: HTMLElement["closest"] } | null;
    if (typeof el?.closest !== "function") return;
    const box = el.closest<HTMLElement>("[data-grid-item]");
    box?.querySelector<HTMLInputElement>('input[type="file"]')?.click();
  });
  return <html.div ref={anchor} style={styles.bridge} />;
}

/**
 * The package's DefaultGridItem wired to begin a cross-grid drag on
 * pointer-down (grid 0.8.42). The starter comes from
 * SitemapInteractionContext — shared by the canvas across xyflow's node
 * boundary — NOT a module singleton, so it survives the bundler splitting the
 * editor across chunks. A section (contentType "grid") drags as a "container"
 * between page grids; a leaf widget as an "app" between section grids. No
 * handoff in preview/publish (no interaction there). This is a COMPONENT (not
 * an inline closure) so it can read the context via a hook.
 *
 * Grid 0.9.4 killed the local DefaultGridItem fork this used to be (the
 * package exposed no overflow seam, so the whole chrome was replicated):
 * - `overflow` prop: a CONTAINER block never clips — a grid item that is the
 *   PARENT of other grid items has children that legitimately paint past it
 *   mid-interaction (a dragged widget travels, telegraph rings bleed). Only
 *   LEAF widgets keep the containment clip (`div[data-grid-item] > div` was
 *   the last clipping ancestor on a dragged widget's escape path —
 *   user-diagnosed 2026-07-16).
 * - lift/selection chrome (interaction shadow + opacity, telegraph outlines
 *   at `--grid-item-outline-offset`, see gridSelection.css) is the
 *   package's — the hand-rolled lift card and cursor logic are gone.
 * The remaining app concerns live on the inner content div: the ⌥Alt
 * cross-tab NATIVE drag arming (the two drag pipelines are mutually
 * exclusive per gesture — while Alt is held the content div is `draggable`
 * and the pointer pipeline stands down; docs/plans/2026-07-17-cross-tab-drag.md), its
 * copy cursor + ⊕ badge telegraph.
 */
/** Kinds whose second click types into their text (select-first): their
 *  quick-edit card opens from the bar's "Quick edit" verb instead. */
const TEXT_KINDS = new Set(["text", "heading", "card"]);

/**
 * The second click on an already selected widget opens its quick-edit card
 * (the level-editor layer). Read on a WINDOW pointerup, as ImageWidget does:
 * the press bubbles to the grid item, which retargets the release, so an
 * element click never fires. `wasSelected` is read at pointerdown, before the
 * grid's select-on-press, so a first click only selects.
 */
function armQuickEdit(
  e: ReactPointerEvent,
  gridId: string,
  itemId: string,
  contentType: string,
): void {
  if (TEXT_KINDS.has(contentType) || !registeredWidgetQuickEdit(contentType))
    return;
  const sel = useGridStore.getState().selection;
  const wasSelected =
    !!sel && sel.gridId === gridId && sel.itemIds.includes(itemId);
  if (!wasSelected) return;
  const sx = e.clientX;
  const sy = e.clientY;
  const finish = (up: PointerEvent) => {
    window.removeEventListener("pointerup", finish);
    window.removeEventListener("pointercancel", cancel);
    // The grid's drag threshold: farther was a move, not a click.
    if (Math.hypot(up.clientX - sx, up.clientY - sy) > 8) return;
    openQuickEdit(gridId, itemId);
  };
  const cancel = () => {
    window.removeEventListener("pointerup", finish);
    window.removeEventListener("pointercancel", cancel);
  };
  window.addEventListener("pointerup", finish);
  window.addEventListener("pointercancel", cancel);
}

function placementOf(contentProps: unknown) {
  const v = (contentProps as { valign?: unknown } | undefined)?.valign;
  return v === "middle" ? styles.middle : v === "bottom" ? styles.bottom : null;
}

function CrossGridItem({
  args,
  gridId,
  preview,
  children,
}: {
  args: GridItemRenderArgs;
  gridId: string;
  preview: boolean;
  children: ReactNode;
}): ReactElement {
  const { startCrossGridDrag } = useSitemapInteraction();
  const { itemId, contentType, contentProps } = args;
  const altHeld = useAltHeld();
  const siteId = useSiteId();
  const dragImgRef = useRef<DragImage | null>(null);
  const crossTabArmed =
    !preview && altHeld && isCrossTabDraggable({ id: itemId, contentType });
  const onPointerDown = preview
    ? undefined
    : (e: ReactPointerEvent) => {
        if (e.altKey && isCrossTabDraggable({ id: itemId, contentType }))
          return;
        startCrossGridDrag(e, {
          itemId,
          fromGridId: gridId,
          kind: contentType === "grid" ? "container" : "app",
        });
      };
  const onDragStart = (e: ReactDragEvent) => {
    // A drag from inside live text editing is a selection drag — not ours.
    if (
      e.target instanceof Element &&
      e.target.closest('[contenteditable="true"]')
    ) {
      e.preventDefault();
      return;
    }
    const item = useGridStore.getState().grids[gridId]?.items[itemId];
    // Layout-px size (offset* ignores canvas zoom) — the receiver refits it
    // against ITS grid geometry so the landing slot previews the true size.
    const host = e.currentTarget as HTMLElement;
    const payload =
      item && siteId
        ? buildCrossTabPayload(item, siteId, window.location.origin, {
            w: host.offsetWidth,
            h: host.offsetHeight,
          })
        : null;
    const json = payload ? encodePayload(payload) : null;
    if (!payload || !json) {
      e.preventDefault();
      return;
    }
    e.stopPropagation();
    const dt = e.dataTransfer;
    dt.effectAllowed = "copy";
    // setData + setDragImage must both happen synchronously in dragstart.
    // Full payload readable at drop; the mini-spec rides the TYPE STRING so
    // the receiving tab can preview during dragover (protected mode).
    dt.setData(WB_ITEM_TYPE, json);
    dt.setData(encodeMetaType(payload), "1");
    const text = textFallbackOf(payload.item.contentProps);
    if (text) dt.setData("text/plain", text);
    const img = prepareDragImage(itemId);
    if (img) {
      const rect = e.currentTarget.getBoundingClientRect();
      // Grab offsets mapped into the IMAGE's layout-px space (the on-screen
      // rect is canvas-zoom-scaled; the drag image is layout-size).
      const dx = ((e.clientX - rect.left) / (rect.width || 1)) * img.w;
      const dy = ((e.clientY - rect.top) / (rect.height || 1)) * img.h;
      dt.setDragImage(img.el, dx, dy);
      dragImgRef.current = img;
      // The browser snapshots the bitmap at dragstart — safe to remove next
      // tick (dragend also disposes, for belt and braces).
      setTimeout(img.dispose, 0);
    }
  };
  const onDragEnd = () => {
    dragImgRef.current?.dispose();
    dragImgRef.current = null;
  };
  // An Inline ("inline-grid") is a container too: its blocks are dragged
  // within and out of its one row, and clipping here cut a dragged block off
  // at the row's edge (seen at the preview, 2026-10-07).
  const isContainer = contentType === "grid" || contentType === "inline-grid";
  return (
    <DefaultGridItem
      args={args}
      onPointerDown={onPointerDown}
      overflow={isContainer ? "visible" : "hidden"}
    >
      {/* Raw <div>: the ⌥ cross-tab copy is NATIVE HTML5 drag-and-drop, and
          react-strict-dom's html.div exposes neither `draggable` nor the
          drag events (they have no native counterpart). */}
      {/* eslint-disable-next-line duro/no-raw-html-element -- native DnD props (draggable/onDragStart) are not part of html.div */}
      <div
        draggable={crossTabArmed || undefined}
        // Every press, not only a drag: in a select-first grid the Box calls
        // its onPointerDown (above) only once a drag starts, so a plain click
        // never reaches it. This runs first, before the grid's select-on-press.
        onPointerDown={
          preview
            ? undefined
            : (e) => armQuickEdit(e, gridId, itemId, contentType)
        }
        onDragStart={crossTabArmed ? onDragStart : undefined}
        onDragEnd={crossTabArmed ? onDragEnd : undefined}
        {...css.props(
          styles.content,
          placementOf(contentProps),
          crossTabArmed && styles.contentArmed,
        )}
      >
        {children}
        {/* ⌥ arming telegraph: while Alt is held, draggable tiles show the
            copy affordance BEFORE the user commits to the gesture (the OS
            copy cursor then carries through the native drag itself). */}
        {crossTabArmed && <html.div style={styles.copyBadge}>⊕ copy</html.div>}
      </div>
    </DefaultGridItem>
  );
}

export function makeRenderWidget(gridId: string, preview: boolean) {
  return function renderWidget(args: GridItemRenderArgs) {
    // grid 0.8: renderItem receives a single args object and must render the
    // pre-bound `Box` itself; CrossGridItem wraps DefaultGridItem (the
    // package chrome) so the widgets keep the classic look AND gain
    // cross-grid drag.
    const { itemId, contentType, contentProps } = args;
    const Widget = resolveWidget(contentType);
    if (!Widget) {
      return (
        <CrossGridItem args={args} gridId={gridId} preview={preview}>
          <html.div style={styles.unknown}>
            Unknown widget: {contentType}
          </html.div>
        </CrossGridItem>
      );
    }
    return (
      <CrossGridItem args={args} gridId={gridId} preview={preview}>
        <DropReveal itemId={itemId}>
          <Widget
            itemId={itemId}
            contentType={contentType}
            contentProps={{ ...(contentProps ?? {}), preview }}
            gridId={gridId}
          />
        </DropReveal>
        {/* Mobile authoring chrome: override badge + hide toggle (self-hides
            off the mobile breakpoint). Not in preview/publish renders. */}
        {!preview && <MobileItemChrome gridId={gridId} itemId={itemId} />}
        {!preview && (
          <EditRequestBridge
            itemId={itemId}
            contentType={contentType}
            contentProps={contentProps}
          />
        )}
      </CrossGridItem>
    );
  };
}
